import React, { useMemo, useState } from "react";
import styles from "./CreditsListing.module.scss";
import LayoutHeader from "@features/common/header/layoutHeader/LayoutHeader";
import { Button, CircularProgress, Pagination } from "@mui/material";
import ExcelUploadSummary from "@features/issueCredits/excelUploadSummary/ExcelUploadSummary";
import ContactsTable from "@features/issueCredits/contactsTable/ContactsTable";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import {
  useDeleteExcelUploadItemMutation,
  useExcelUploadActionMutation,
  useFetchExcelUploadListQuery,
} from "@features/issueCredits/issueCreditsAPI";
import {
  notifyUser,
  selectDashboardData,
  selectUploadListSnapshot,
} from "@redux/slices/CommonSlice";
import ConfirmListItemModal from "@features/common/modal/confirmListItemModal/ConfirmListItemModal";
import ClearAllModal from "@features/common/modal/clearAllModal/ClearAllModal";
import { useNavigate } from "react-router-dom";
import { PAGE_URLS } from "@constants/common";
import PageLoader from "@features/common/pageLoader/PageLoader";
import SubmissionErrorModal from "@features/common/modal/submissionErrorModal/SubmissionErrorModal";
import ConfirmModal from "@features/drafts/confirmModal/ConfirmModal";

const CreditsListing: React.FC = () => {
  /* --------------------------------------------------------------------------
   * Store / Navigation
   * -------------------------------------------------------------------------- */
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const urlLists = useAppSelector(selectUploadListSnapshot);
  const dashboardData = useAppSelector(selectDashboardData);

  /* --------------------------------------------------------------------------
   * Derived API Endpoints
   * -------------------------------------------------------------------------- */
  const summaryUrl = urlLists?.summary;
  const saveUrl = urlLists?.save;
  const submitUrl = urlLists?.submit;
  const clearAllUrl = urlLists?.clear_all;

  /* --------------------------------------------------------------------------
   * Local UI State
   * -------------------------------------------------------------------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [clearAllModalOpen, setClearAllModalOpen] = useState(false);
  const [submitErrorModal, setSubmitErrorModal] = useState({
    show: false,
    message: "",
  });
  const [onEditStateChange, setOnEditStateChange] = useState(false);

  /* --------------------------------------------------------------------------
   * List URL (includes pagination & search)
   * -------------------------------------------------------------------------- */
  const listUrl = urlLists?.list
    ? `${urlLists.list}?page=${currentPage}${
        searchTerm ? `&q=${encodeURIComponent(searchTerm)}` : ""
      }`
    : "";

  /* --------------------------------------------------------------------------
   * Queries: List & Summary
   * -------------------------------------------------------------------------- */
  const {
    data: listData,
    isLoading: listLoading,
    refetch: listRefetch,
  } = useFetchExcelUploadListQuery(
    { url: listUrl, method: "GET" },
    { skip: !urlLists?.list },
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    refetch: summaryRefetch,
  } = useFetchExcelUploadListQuery(
    { url: summaryUrl, method: "GET" },
    { skip: !summaryUrl },
  );

  /* --------------------------------------------------------------------------
   * Mutations
   * -------------------------------------------------------------------------- */
  const [deleteExcelUploadItem, { isLoading: isDeleting }] =
    useDeleteExcelUploadItemMutation();
  const [editExcelUploadItem, { isLoading: isEditing }] =
    useExcelUploadActionMutation();
  const [saveAction, { isLoading: isSaving }] = useExcelUploadActionMutation();
  const [submitAction, { isLoading: isSubmitting }] =
    useExcelUploadActionMutation();
  const [clearAllAction, { isLoading: isClearingAll }] =
    useExcelUploadActionMutation();

  /* --------------------------------------------------------------------------
   * Memoized Maps (Edit/Delete URLs per row)
   * -------------------------------------------------------------------------- */
  const editUrlMap = useMemo(
    () =>
      (listData?.data || []).reduce((acc: Record<number, string>, r: any) => {
        acc[r.id] = r.actions?.url_update;
        return acc;
      }, {}),
    [listData],
  );

  const deleteUrlMap = useMemo(
    () =>
      (listData?.data || []).reduce((acc: Record<number, string>, r: any) => {
        acc[r.id] = r.actions?.url_delete;
        return acc;
      }, {}),
    [listData],
  );

  /* --------------------------------------------------------------------------
   * Handlers: Modals
   * -------------------------------------------------------------------------- */
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);
  const openClearAllModal = () => setClearAllModalOpen(true);
  const closeClearAllModal = () => setClearAllModalOpen(false);
  const openSubmitErrorModal = (message: string) =>
    setSubmitErrorModal({ show: true, message });
  const closeSubmitErrorModal = () =>
    setSubmitErrorModal({ show: false, message: "" });

  /* --------------------------------------------------------------------------
   * Handlers: Search
   * -------------------------------------------------------------------------- */
  const handleSearchChange = (val: string) => {
    setCurrentPage(1);
    setSearchTerm(val);
  };
  const handleClearSearch = () => {
    if (searchTerm) {
      setSearchTerm("");
      setCurrentPage(1);
    }
  };

  /* --------------------------------------------------------------------------
   * Notification Helper
   * -------------------------------------------------------------------------- */
  const showSuccessNotification = () => {
    dispatch(
      notifyUser({
        title: "Submission Successful",
        message: "Your data has been successfully submitted.",
      }),
    );
  };

  /* --------------------------------------------------------------------------
   * Row Actions
   * -------------------------------------------------------------------------- */
  const handleEditSave = async (id: number, updatedData: any) => {
    const urlEdit = editUrlMap[id];
    if (!urlEdit) return;
    try {
      await editExcelUploadItem({
        url: urlEdit,
        method: "PATCH",
        body: updatedData,
      }).unwrap();
    } catch (err: any) {
      if (err?.status === 400) {
        let currencyError = "";
        if (err?.data?.errors?.currency) {
          currencyError = Array.isArray(err.data.errors.currency)
            ? err.data.errors.currency[0]
            : err.data.errors.currency;
        } else if (err?.data?.currency) {
          currencyError = err.data.currency;
        }
        dispatch(
          notifyUser({
            title: err?.data?.message,
            message: currencyError || "Invalid currency value.",
            severity: "error",
          }),
        );
        throw new Error(
          currencyError || err?.data?.message || "Invalid currency value.",
        );
      }
      throw err;
    } finally {
      // Always refetch, even on error
      await Promise.all([listRefetch(), summaryRefetch()]);
    }
  };

  const handleRemove = async (id: number) => {
    // Check if this is the last entry
    const totalEntries = listData?.data?.length || 0;

    if (totalEntries === 1 && clearAllUrl) {
      // If only one entry remaining, call clear_all instead
      try {
        await clearAllAction({ url: clearAllUrl, method: "DELETE" }).unwrap();
        navigate(PAGE_URLS.ISSUE_CREDITS);
      } catch (err) {
        console.error("Clear All error:", err);
      }
    } else {
      // Normal delete flow
      const urlDelete = deleteUrlMap[id];
      if (!urlDelete) return;
      try {
        const response = await deleteExcelUploadItem(urlDelete).unwrap();
        dispatch(
          notifyUser({
            title: "Successful",
            message: response.message || "Your entry has been deleted",
          }),
        );
        listRefetch();
        summaryRefetch();
      } catch (err) {
        console.error("Delete error:", err);
      }
    }
  };

  /* --------------------------------------------------------------------------
   * Bulk Actions (Save / Submit / Clear All)
   * -------------------------------------------------------------------------- */
  const handleSave = async () => {
    if (!saveUrl) return;
    try {
      await saveAction({ url: saveUrl, method: "PATCH" }).unwrap();
      navigate(PAGE_URLS.DRAFTS);
    } catch (error: any) {
      if (error?.status === 400) {
        dispatch(
          notifyUser({
            title: "Maximum Limit Exceeded",
            message: error?.data?.message,
            severity: "error",
          }),
        );
      }
    }
  };

  const handleSubmit = async () => {
    if (!submitUrl) return;
    try {
      await submitAction({ url: submitUrl, method: "POST" }).unwrap();
      showSuccessNotification();
      navigate(PAGE_URLS.ISSUE_CREDITS_SUCCESS);
      closeConfirmModal();
    } catch (error: any) {
      openSubmitErrorModal(error?.data?.message);
      closeConfirmModal();
    }
  };

  const handleClearAll = async () => {
    if (!clearAllUrl) return;
    try {
      await clearAllAction({ url: clearAllUrl, method: "DELETE" }).unwrap();
      navigate(PAGE_URLS.ISSUE_CREDITS);
    } catch (error) {
      console.error("Clear All error:", error);
    }
  };

  const handleEditStateChange = (isEditing: boolean) => {
    setOnEditStateChange(isEditing);
  };

  /* --------------------------------------------------------------------------
   * Loading State
   * -------------------------------------------------------------------------- */
  if (listLoading) return <PageLoader />;

  /* --------------------------------------------------------------------------
   * Render
   * -------------------------------------------------------------------------- */
  return (
    <div className={styles.pageContainer}>
      <LayoutHeader
        currency={dashboardData?.currency}
        amount={dashboardData?.amount}
        headerTitle="Issue Credits"
      />

      <div className={styles["excel-upload"]}>
        <ExcelUploadSummary
          fileName={summaryData?.data?.file_name}
          totalEntries={summaryData?.data?.total_entries}
          successEntries={summaryData?.data?.total_success}
          errorEntries={summaryData?.data?.total_errors}
          currency={summaryData?.data?.currency}
          totalCreditValue={summaryData?.data?.total_credit}
          loading={summaryLoading}
        />

        <ContactsTable
          rows={listData?.data}
          onRemove={handleRemove}
          onEditSave={handleEditSave}
          isDeleting={isDeleting}
          excel={true}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
          isSearching={listLoading}
          isEditing={isEditing}
          onEditStateChange={onEditStateChange}
          handleEditStateChange={handleEditStateChange}
        />

        <div className={styles["excel-upload__pagination"]}>
          <Pagination
            count={listData?.total_page || 0}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            shape="rounded"
            siblingCount={0}
          />
          <span className={styles["excel-upload__page-info"]}>
            of {listData?.total_page || 0} pages
          </span>
        </div>

        {confirmModalOpen && (
          <ConfirmModal
            onClose={closeConfirmModal}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            warnings={summaryData?.data?.warnings ?? []}
          />
        )}
        {submitErrorModal.show && (
          <SubmissionErrorModal
            onClose={closeSubmitErrorModal}
            message={submitErrorModal.message}
          />
        )}

        {clearAllModalOpen && (
          <ClearAllModal
            onClose={closeClearAllModal}
            onClearAll={handleClearAll}
            isClearingAll={isClearingAll}
          />
        )}

        <div
          className={`${styles["excel-upload__button-wrapper"]} excel-table-buttons`}
        >
          <div
            className={`${styles["excel-upload__button-wrapper__inner"]} save-submit-buttons`}
          >
            <Button
              variant="outlined"
              sx={{ backgroundColor: "#fff", color: "#0e0f0c" }}
              onClick={handleSave}
              loading={isSaving}
              disabled={onEditStateChange}
            >
              {isSaving ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#0e0f0c", color: "#fff" }}
              onClick={openConfirmModal}
              loading={isSubmitting}
              disabled={
                summaryData?.data?.total_errors > 0 || onEditStateChange
              }
            >
              Submit
            </Button>
          </div>
          <a onClick={openClearAllModal}>Clear All</a>
        </div>
      </div>
    </div>
  );
};

export default CreditsListing;
